/**
 * Настройка pdf.js для просмотра страниц разметки (§7.1, §4.2).
 *
 * ## Байты приходят через наш эндпоинт, а не по presigned URL
 *
 * `PREVIEW_MODE=pdfjs` — режим по умолчанию: страницы рендерит браузер, а байты
 * он получает по адресу `/api/v1/files/{id}/content` — same-origin, под cookie
 * сессии, с поддержкой `Range`. Presigned URL наружу не выдаётся никогда: он
 * утекает в историю браузера и обходит RBAC на время своего TTL, а стандарт сам
 * относит его к секретам. Никакого другого источника байтов в этом модуле нет и
 * появиться не должно — `getDocument` вызывается только с нашим путём.
 *
 * ## «Запуск без исполнения встроенного JavaScript» в версии 6.2.108
 *
 * План называет опцию `isEvalSupported`. В pdf.js 6 её НЕТ: проверено прямым
 * поиском по `build/pdf.mjs` и `build/pdf.worker.mjs` — ноль совпадений, а в
 * `DocumentInitParameters` такого поля не объявлено. Библиотека перестала
 * использовать `eval` вовсе, поэтому опция, которой можно было бы это запретить,
 * исчезла вместе с причиной. Ставить её «на всякий случай» нельзя: при
 * `exactOptionalPropertyTypes` это ошибка типа, а в рантайме — молча
 * игнорируемое поле, создающее видимость защиты.
 *
 * Требование плана в этой версии обеспечивается тремя настройками, каждая из
 * которых что-то действительно выключает:
 *
 * * `enableXfa: false` — XFA это форма, поведение которой задаётся скриптом;
 *   выключение движка XFA и есть отказ от исполнения встроенной логики;
 * * `annotationMode: AnnotationMode.DISABLE` при рендере — аннотации не
 *   исполняются и не рисуются, значит `/OpenAction`, `/AA` и `/JS` в них
 *   недостижимы;
 * * слой аннотаций не строится вовсе, а `docBaseUrl` не задаётся, поэтому
 *   относительная ссылка внутри документа не может быть восстановлена в
 *   абсолютную и открыта. Авто-открытия внешних ссылок в портале нет, потому что
 *   нет кода, который бы их открывал: страница рисуется в `<canvas>`, и
 *   кликабельных элементов документа на ней не существует.
 *
 * ## Range, а не «скачать 86 МБ»
 *
 * `disableStream: true` вместе с `disableAutoFetch: true` — это условие того,
 * что по сети идут единицы мегабайт: без выключенного стриминга pdf.js
 * продолжает докачивать документ целиком, и опция `disableAutoFetch`, по её
 * собственной документации, не работает.
 *
 * ## Четыре адреса ресурсов: почему без них ломается ровно то, что ломалось
 *
 * pdf.js не носит в бандле ни шрифты, ни таблицы CMap, ни wasm-декодеры: он
 * догружает их по адресам из параметров. Незаданный адрес — не «настройка по
 * умолчанию», а исключение из фабрики двоичных данных: `Ensure that the
 * '<kind>' API parameter is provided`. Отсюда два наблюдавшихся дефекта.
 *
 * * `standardFontDataUrl`. Пара к `useSystemFonts: false` ниже. При
 *   `useSystemFonts: true` воркер выходит из `fetchStandardFontData` рано и за
 *   данными не идёт; при `false` — идёт на КАЖДЫЙ стандартный шрифт документа.
 *   Неудача при этом не кэшируется (в pdf.js `catch { warn; return null }`
 *   без записи в кэш), поэтому предупреждение повторялось на каждый шрифт,
 *   каждый рендер, каждое переключение страницы и каждую смену масштаба —
 *   сотни строк в консоли и лишний round-trip воркер↔страница перед каждой
 *   отрисовкой. Выключать `useSystemFonts` при этом по-прежнему правильно (см.
 *   ниже) — недоставало именно парного адреса, а не самого флага.
 * * `wasmUrl`. `useWasm` включён по умолчанию, а изображения JPEG2000
 *   (`/Filter /JPXDecode`) и JBIG2 декодируются только через wasm. В
 *   комплектах ИД такие изображения есть, и без этого адреса страница с ними
 *   не отрисовывалась: сначала исключение фабрики, затем провал
 *   fallback-импорта, склеенного из строки `"null"` и имени файла.
 * * `cMapUrl` + `cMapPacked`. Кодировки Identity/CJK. В текущем корпусе не
 *   встречались, но отсутствие адреса даёт ту же ошибку и тот же нечитаемый
 *   текст, а стоимость параметра — ноль.
 * * `iccUrl`. Без него pdf.js предупреждает об отсутствии поддержки ICC и
 *   рисует цвета мимо профиля.
 *
 * Файлы кладёт в сборку собственный плагин `id-pdfjs-assets`
 * (`apps/web/vite.config.ts`), он же раздаёт их в режиме разработки; nginx
 * образа отдаёт `/pdfjs/` отдельным `location` (`apps/web/nginx.conf`).
 * Внешних адресов здесь нет и быть не может — портал работает в контуре без
 * них, поэтому и CDN pdf.js не используется.
 */
import {
  AnnotationMode,
  getDocument,
  GlobalWorkerOptions,
  type PDFDocumentProxy,
  type PDFPageProxy,
} from 'pdfjs-dist';
// Воркер отдаётся сборщиком как отдельный ресурс: `?url` даёт адрес файла,
// который Vite положит в сборку. Загружать воркер с CDN нельзя — портал
// работает в контуре без внешних адресов.
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

/**
 * Метка эпохи кэша в адресе воркера.
 *
 * Не версия и не отпечаток содержимого — их даёт само имя файла, которое Vite
 * строит из хэша. Метка нужна ради ОДНОГО прошлого события и остаётся навсегда.
 *
 * Воркер раздавался с неверным типом содержимого: `.mjs` нет в `mime.types`
 * образа nginx, и файл уезжал как `application/octet-stream`, а модульный скрипт
 * с таким типом браузер обязан отвергнуть. Тип починен в `apps/web/nginx.conf`,
 * но одного этого мало: ответ раздаётся с `Cache-Control: public, immutable,
 * max-age=2592000`, а `immutable` запрещает браузеру ревалидировать запись даже
 * при обычной перезагрузке. Имя файла от правки nginx не меняется — значит
 * браузер, успевший забрать испорченный ответ, брал бы его из своего кэша ещё
 * тридцать дней и на исправленный сервер не пошёл бы вовсе.
 *
 * Другой адрес — единственное, что этот кэш прерывает: ключ записи в кэше
 * включает строку запроса. На стороне сервера ничего не меняется — nginx
 * сопоставляет `location` с путём, строка запроса в сопоставлении не участвует,
 * поэтому и тип, и заголовки кэширования остаются прежними.
 *
 * Менять метку впредь не нужно: при изменении содержимого меняется имя файла.
 */
const WORKER_CACHE_EPOCH = '2026-08-23';

GlobalWorkerOptions.workerSrc = `${workerUrl}?cache=${WORKER_CACHE_EPOCH}`;

/**
 * Префикс раздачи ресурсов pdf.js.
 *
 * Считается от `BASE_URL`, а не пишется как `/pdfjs/`: портал может быть
 * смонтирован не в корень, и абсолютный литерал увёл бы запросы на чужой адрес.
 * Завершающая косая черта обязательна — pdf.js склеивает адрес конкатенацией,
 * без неё имя файла прилипло бы к имени каталога.
 */
const ASSET_BASE = `${import.meta.env.BASE_URL.replace(/\/$/, '')}/pdfjs/`;

/** Режим аннотаций при рендере: не исполнять и не рисовать. */
export const SAFE_ANNOTATION_MODE: number = AnnotationMode.DISABLE;

/**
 * Кэш открытых документов на файл.
 *
 * Экран разметки листает страницы одного комплекта, и открывать документ заново
 * на каждый переход означало бы заново читать таблицу xref по сети. Ключ —
 * идентификатор файла: содержимое неизменяемо (§3.3), поэтому кэш не может
 * устареть.
 *
 * Хранится ПАРА «задача загрузки + её промис», а не один промис. Освобождение
 * (`destroy`) объявлено у задачи загрузки, а не у `PDFDocumentProxy`, и без
 * ссылки на задачу закрыть воркер было бы нечем — именно поэтому раньше здесь
 * звался `cleanup()`, который воркер не трогает вовсе.
 */
interface OpenedDocument {
  readonly task: { destroy: () => Promise<void> };
  readonly promise: Promise<PDFDocumentProxy>;
}

const documents = new Map<string, OpenedDocument>();

export function openDocument(fileId: string, contentUrl: string): Promise<PDFDocumentProxy> {
  const cached = documents.get(fileId);
  if (cached !== undefined) return cached.promise;

  const loading = getDocument({
    url: contentUrl,
    // Форма XFA не рендерится: см. заголовок модуля.
    enableXfa: false,
    // Оба флага нужны вместе, иначе докачка продолжится.
    disableStream: true,
    disableAutoFetch: true,
    // Шрифты, которых нет в документе, не подменяются системными: это лишний
    // источник расхождения того, что видит разметчик, с тем, что распознаёт OCR.
    // Парный к этому флагу адрес — `standardFontDataUrl` ниже; без него флаг
    // означал не «взять свои шрифты», а «не взять никакие» (см. шапку модуля).
    useSystemFonts: false,
    // Адреса ресурсов, которые pdf.js догружает сам (см. шапку модуля).
    standardFontDataUrl: `${ASSET_BASE}standard_fonts/`,
    cMapUrl: `${ASSET_BASE}cmaps/`,
    // Таблицы в `pdfjs-dist/cmaps` лежат в упакованном виде (`.bcmap`).
    cMapPacked: true,
    wasmUrl: `${ASSET_BASE}wasm/`,
    iccUrl: `${ASSET_BASE}iccs/`,
    // `docBaseUrl` не задаётся намеренно: без него относительная ссылка внутри
    // документа не восстанавливается в абсолютную.
  });

  const promise = loading.promise;
  documents.set(fileId, { task: loading, promise });
  // Неудачное открытие не должно застревать в кэше: следующий заход обязан
  // попробовать снова, иначе один сетевой сбой закрывает файл до перезагрузки.
  promise.catch(() => documents.delete(fileId));
  return promise;
}

/**
 * Освобождение документов: вызывается при уходе с экрана разметки.
 *
 * `destroy()`, а не `cleanup()`. Второй сбрасывает лишь промежуточные данные
 * отрисовки, оставляя жить и `PDFDocumentProxy`, и его воркер, — то есть кэш
 * очищался, а память нет, и следующий заход на экран поднимал ВТОРОЙ комплект
 * поверх первого. Это ровно та утечка, о которой говорит вызывающий
 * (`usePdfCacheCleanup`), и до сих пор она не закрывалась.
 */
export async function closeDocuments(): Promise<void> {
  const opened = [...documents.values()];
  documents.clear();
  await Promise.all(
    opened.map(async (entry) => {
      try {
        // Отклонение самого промиса гасится здесь же: `destroy()` его отвергает,
        // если документ ещё не открылся, и это штатный исход ухода с экрана.
        entry.promise.catch(() => undefined);
        await entry.task.destroy();
      } catch {
        // Документ не открылся либо уже освобождён — освобождать нечего.
      }
    }),
  );
}

export type { PDFDocumentProxy, PDFPageProxy };
